import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './components/AuthProvider'
import { ProtectedRoute } from './components/ProtectedRoute'
import { PublicRoute } from './components/PublicRoute'
import { ErrorBoundary } from './components/ErrorBoundary'
import { OfflineBanner } from './components/OfflineBanner'
import { InstallPrompt } from './components/InstallPrompt'

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <svg
          className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  )
}

// Lazy-loaded page components for code splitting
const LoginPage = lazy(() =>
  import('./pages/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
)
const SignUpPage = lazy(() =>
  import('./pages/auth/SignUpPage').then((m) => ({ default: m.SignUpPage }))
)

// Coordinator pages
const CoordinatorDashboard = lazy(() =>
  import('./pages/coordinator/CoordinatorDashboard').then((m) => ({
    default: m.CoordinatorDashboard,
  }))
)
const CreateEventPage = lazy(() =>
  import('./pages/coordinator/CreateEventPage').then((m) => ({
    default: m.CreateEventPage,
  }))
)
const EventSetupPage = lazy(() =>
  import('./pages/coordinator/EventSetupPage').then((m) => ({
    default: m.EventSetupPage,
  }))
)
const DriversPage = lazy(() =>
  import('./pages/coordinator/DriversPage').then((m) => ({
    default: m.DriversPage,
  }))
)
const JudgesPage = lazy(() =>
  import('./pages/coordinator/JudgesPage').then((m) => ({
    default: m.JudgesPage,
  }))
)
const LeaderboardPage = lazy(() =>
  import('./pages/coordinator/LeaderboardPage').then((m) => ({
    default: m.LeaderboardPage,
  }))
)

// Judge pages
const JudgeDashboard = lazy(() =>
  import('./pages/judge/JudgeDashboard').then((m) => ({
    default: m.JudgeDashboard,
  }))
)
const DriverSelectPage = lazy(() =>
  import('./pages/judge/DriverSelectPage').then((m) => ({
    default: m.DriverSelectPage,
  }))
)
const ScoringPage = lazy(() =>
  import('./pages/judge/ScoringPage').then((m) => ({
    default: m.ScoringPage,
  }))
)

// Offline page
const OfflinePage = lazy(() =>
  import('./pages/OfflinePage').then((m) => ({
    default: m.OfflinePage,
  }))
)

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          {/* Offline status banner */}
          <OfflineBanner />

          {/* Main routes wrapped in Suspense for code splitting */}
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes - redirect to dashboard if authenticated */}
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignUpPage />
                  </PublicRoute>
                }
              />

              {/* Offline page */}
              <Route path="/offline" element={<OfflinePage />} />

              {/* Coordinator routes */}
              <Route
                path="/coordinator"
                element={
                  <ProtectedRoute allowedRoles={['coordinator']}>
                    <CoordinatorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coordinator/events/new"
                element={
                  <ProtectedRoute allowedRoles={['coordinator']}>
                    <CreateEventPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coordinator/events/:eventId"
                element={
                  <ProtectedRoute allowedRoles={['coordinator']}>
                    <EventSetupPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coordinator/events/:eventId/drivers"
                element={
                  <ProtectedRoute allowedRoles={['coordinator']}>
                    <DriversPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coordinator/drivers"
                element={
                  <ProtectedRoute allowedRoles={['coordinator']}>
                    <DriversPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coordinator/events/:eventId/judges"
                element={
                  <ProtectedRoute allowedRoles={['coordinator']}>
                    <JudgesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coordinator/events/:eventId/leaderboard"
                element={
                  <ProtectedRoute allowedRoles={['coordinator']}>
                    <LeaderboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Judge routes */}
              <Route
                path="/judge"
                element={
                  <ProtectedRoute allowedRoles={['judge']}>
                    <JudgeDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/judge/events/:eventId/drivers"
                element={
                  <ProtectedRoute allowedRoles={['judge']}>
                    <DriverSelectPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/judge/events/:eventId/score/:driverId/:testType"
                element={
                  <ProtectedRoute allowedRoles={['judge']}>
                    <ScoringPage />
                  </ProtectedRoute>
                }
              />

              {/* Catch all - redirect to login */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>

          {/* Install prompt for PWA */}
          <InstallPrompt />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
